import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../contexts/FamilyContext';
import { api } from '../App';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Users, Crown, Mail, Copy, Trash2, UserPlus, Settings, 
  Check, Shield, Clock, RefreshCw, ChevronDown
} from 'lucide-react';

const FamilySettings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { currentFamily, isAdmin, isPremium, planDetails, refreshFamilyData } = useFamily();
  
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get('/families/current/members'),
        isAdmin ? api.get('/invites') : Promise.resolve({ data: [] })
      ]);
      setMembers(membersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    
    // Check member limit
    if (!isPremium && members.length + invites.length >= 4) {
      toast.error('Member limit reached. Upgrade to Premium for unlimited members.');
      return;
    }
    
    setInviting(true);
    try {
      const res = await api.post('/invites', { 
        email: inviteEmail || null, 
        role: inviteRole 
      });
      
      toast.success(
        inviteEmail 
          ? `Invite sent to ${inviteEmail}` 
          : 'Invite code generated!'
      );
      
      // Show the invite code
      if (!inviteEmail) {
        toast.info(`Invite code: ${res.data.invite_code}`, { duration: 10000 });
      }
      
      setInviteEmail('');
      setShowInviteForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.delete(`/invites/${inviteId}`);
      toast.success('Invite cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/families/current/members/${userId}/role`, null, {
        params: { role: newRole }
      });
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the family?`)) return;
    
    try {
      await api.delete(`/families/current/members/${userId}`);
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  if (!isAdmin) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-12">
          <Shield size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Admin Access Required</h2>
          <p className="text-gray-500 mt-2">Only family admins can access settings.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-primary mt-4">
            Go to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Family Settings</h1>
            <p className="text-gray-500 mt-1">{currentFamily?.name}</p>
          </div>
          <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Plan Info Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isPremium ? 'bg-amber-100' : 'bg-gray-100'
                }`}>
                  <Crown size={24} className={isPremium ? 'text-amber-600' : 'text-gray-400'} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">
                    {isPremium ? 'Premium Plan' : 'Free Plan'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {members.length} / {isPremium ? '∞' : '4'} members
                  </p>
                </div>
              </div>
              {!isPremium && (
                <button className="btn-primary">
                  Upgrade to Premium
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Members Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Members ({members.length})
            </CardTitle>
            <button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="btn-primary flex items-center gap-2 text-sm"
              disabled={!isPremium && members.length + invites.length >= 4}
            >
              <UserPlus size={16} />
              Invite Member
            </button>
          </CardHeader>
          <CardContent>
            {/* Invite Form */}
            {showInviteForm && (
              <form onSubmit={handleInvite} className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="Leave empty for code-only invite"
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="input-field w-full"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviting}
                    className="btn-primary"
                  >
                    {inviting ? 'Sending...' : (inviteEmail ? 'Send Invite' : 'Generate Code')}
                  </button>
                </div>
              </form>
            )}

            {/* Members List */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <div className="divide-y">
                {members.map((member) => (
                  <div key={member.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-700 font-semibold">
                          {member.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {member.user_name}
                          {member.user_id === user?.id && (
                            <span className="ml-2 text-xs text-gray-500">(You)</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{member.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.role === 'admin' ? (
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full flex items-center gap-1">
                          <Crown size={14} />
                          Admin
                        </span>
                      ) : (
                        <div className="relative">
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                            className="appearance-none bg-gray-100 text-gray-700 text-sm font-medium rounded-full px-3 py-1 pr-8 cursor-pointer hover:bg-gray-200"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                        </div>
                      )}
                      {member.user_id !== user?.id && (
                        <button
                          onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                          title="Remove member"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Invites */}
        {invites.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock size={20} />
                Pending Invites ({invites.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {invites.map((invite) => (
                  <div key={invite.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Mail size={18} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {invite.email || 'Code-only invite'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">
                            {invite.invite_code}
                          </code>
                          <button
                            onClick={() => handleCopyCode(invite.invite_code)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invite.role === 'admin' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {invite.role}
                      </span>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                        title="Cancel invite"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default FamilySettings;
